import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { API_BASE } from '../../config/api';
import { Page } from '../Layout/PageLayout';
import FunnelPanel from './panels/FunnelPanel';
import HeatmapPanel from './panels/HeatmapPanel';
import AnalyticsShell from './AnalyticsShell';
import SessionsView from './views/SessionsView';
import ProjectSettingsView from './views/ProjectSettingsView';
import WorkspaceView from './views/WorkspaceView';
import PlatformView from './views/PlatformView';
import useConfirmDialog from './ConfirmDialog';
import {
  INGEST_ENDPOINT,
  buildRobloxSnippet,
  buildSessionTimeline,
  buildTrackableFieldCatalog,
  buildTrackedFieldSeries,
  formatDurationFromSeconds,
  getRetentionDaysFromPlanId,
  getSessionDurationSeconds,
  getSessionLimitFromPlanId,
  parseDate,
  projectPositionsFromFlushes,
  worldToMapPoint,
} from './analyticsUtils';
import {
  Btn,
  Card,
  CardTitle,
  Empty,
  GhostBtn,
  Hint,
  Mono,
  Notice,
  Row,
  Segment,
  Segmented,
  Skeleton,
  Stack,
  VisuallyHidden,
} from './AnalyticsAdmin.styles';

const RANGE_OPTIONS = [
  { value: '1', label: 'Today' },
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom' },
];

const LoadingLayout = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-height: calc(100vh - var(--header-height));

  .side {
    flex: 1 1 15.5rem;
    padding: 1rem 0.75rem;
    border-right: 1px solid var(--border-subtle);
    background: var(--surface-1);
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
  }

  .main {
    flex: 999 1 40rem;
    padding: 1.5rem 1.75rem;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const CenteredNote = styled.div`
  max-width: 36rem;
  margin: 0 auto;
  padding: 5rem 1.5rem;
`;

// view: 'analytics' (explore) or 'settings' (route /dashboard/analytics/settings → opens Workspace).

const AnalyticsAdmin = ({ view = 'analytics' }) => {
  const [section, setSection] = useState(view === 'settings' ? 'workspace' : 'sessions');
  const [settingsTab, setSettingsTab] = useState('general');
  const [rangeValue, setRangeValue] = useState('7');
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [flushesLoading, setFlushesLoading] = useState(false);
  const [dialog, ask] = useConfirmDialog();
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [plans, setPlans] = useState([]);
  const [workspaceMembers, setWorkspaceMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessInfo, setAccessInfo] = useState({ allowed: false, platformAdmin: false, workspaceIds: [] });

  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceOwnerEmail, setNewWorkspaceOwnerEmail] = useState('');
  const [newWorkspacePlanId, setNewWorkspacePlanId] = useState('starter');
  const [workspaceSaving, setWorkspaceSaving] = useState(false);

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [workspaceMemberEmail, setWorkspaceMemberEmail] = useState('');
  const [workspaceMemberRole, setWorkspaceMemberRole] = useState('viewer');
  const [memberSaving, setMemberSaving] = useState(false);
  const [projectRetentionInput, setProjectRetentionInput] = useState('');
  const [projectRetentionSaving, setProjectRetentionSaving] = useState(false);
  const [projectSessionLimitInput, setProjectSessionLimitInput] = useState('');
  const [projectSessionLimitSaving, setProjectSessionLimitSaving] = useState(false);

  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [keyModal, setKeyModal] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsTotalCount, setSessionsTotalCount] = useState(0);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsPerPage, setSessionsPerPage] = useState(25);
  const [sessionsPage, setSessionsPage] = useState(1);
  const [sessionsSortBy, setSessionsSortBy] = useState('updated_at');
  const [sessionsSortDir, setSessionsSortDir] = useState('desc');
  const [expandedSession, setExpandedSession] = useState(null);
  const [flushes, setFlushes] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [expandedTimelineItems, setExpandedTimelineItems] = useState({});
  const [selectedActionMapKey, setSelectedActionMapKey] = useState(null);
  const [timelinePage, setTimelinePage] = useState(1);
  const [trackedStats, setTrackedStats] = useState([]);
  const [trackedStatsLoading, setTrackedStatsLoading] = useState(false);
  const [trackedStatsSaving, setTrackedStatsSaving] = useState(false);
  const [showTrackedStatBuilder, setShowTrackedStatBuilder] = useState(false);
  const [fieldDiscoveryScope, setFieldDiscoveryScope] = useState('all_sessions');
  const [trackedStatsViewMode, setTrackedStatsViewMode] = useState('all_sessions');
  const [projectDiscoveredFields, setProjectDiscoveredFields] = useState([]);
  const [projectDiscoveredFieldsLoading, setProjectDiscoveredFieldsLoading] = useState(false);
  const [trackedStatSeriesMap, setTrackedStatSeriesMap] = useState({});
  const [trackedStatForm, setTrackedStatForm] = useState({ fieldPath: '', label: '', valueType: 'number', visualization: 'line' });
  const [projectMap, setProjectMap] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapSaving, setMapSaving] = useState(false);
  const [mapBounds, setMapBounds] = useState({ worldMinX: '', worldMaxX: '', worldMinZ: '', worldMaxZ: '', rotationDegrees: 0, flipX: false, flipZ: false });
  const [mapFile, setMapFile] = useState(null);
  const [mapVersion, setMapVersion] = useState(0);
  const [mapImageSize, setMapImageSize] = useState({ width: 0, height: 0 });
  const [debugPosition, setDebugPosition] = useState({ x: '', z: '' });

  const [team, setTeam] = useState({ db: [], envAllowlist: null });
  const [teamLoading, setTeamLoading] = useState(true);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [teamSaving, setTeamSaving] = useState(false);
  const [bulkDeleteAgeDays, setBulkDeleteAgeDays] = useState('7');
  const [bulkDeleteSessionsLoading, setBulkDeleteSessionsLoading] = useState(false);
  const [exportSessionsLoading, setExportSessionsLoading] = useState(false);
  const [exportSessionsMode, setExportSessionsMode] = useState('all');
  const [exportSessionsCount, setExportSessionsCount] = useState('100');
  const [includeAiPromptInExport, setIncludeAiPromptInExport] = useState(true);
  const [includePositionsInExport, setIncludePositionsInExport] = useState(true);

  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => w.id === selectedWorkspaceId) || null,
    [workspaces, selectedWorkspaceId]
  );

  const selectedWorkspaceRetentionDays = useMemo(
    () => Number(selectedWorkspace?.effective_retention_days || getRetentionDaysFromPlanId(selectedWorkspace?.plan_id || selectedWorkspace?.limits?.planId || 'starter')),
    [selectedWorkspace]
  );

  const workspaceProjects = useMemo(
    () => projects.filter((p) => p.workspace_id === selectedWorkspaceId),
    [projects, selectedWorkspaceId]
  );

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) || null,
    [projects, selectedProjectId]
  );

  const selectedProjectRetentionDays = useMemo(
    () => Number(selectedProject?.effective_retention_days || getRetentionDaysFromPlanId(selectedProject?.workspace_plan_id || selectedWorkspace?.plan_id || 'starter')),
    [selectedProject, selectedWorkspace]
  );

  const selectedProjectMaxRetentionDays = useMemo(
    () => Number(selectedProject?.max_retention_days || getRetentionDaysFromPlanId(selectedProject?.workspace_plan_id || selectedWorkspace?.plan_id || 'starter')),
    [selectedProject, selectedWorkspace]
  );

  const selectedProjectSessionLimit = useMemo(
    () => Number(selectedProject?.effective_session_limit || getSessionLimitFromPlanId(selectedProject?.workspace_plan_id || selectedWorkspace?.plan_id || 'starter')),
    [selectedProject, selectedWorkspace]
  );

  const selectedProjectMaxSessionLimit = useMemo(
    () => Number(selectedProject?.max_session_limit || getSessionLimitFromPlanId(selectedProject?.workspace_plan_id || selectedWorkspace?.plan_id || 'starter')),
    [selectedProject, selectedWorkspace]
  );

  const selectedProjectSessionUsage = useMemo(() => {
    const limit = Math.max(1, Number(selectedProjectSessionLimit || 0));
    const count = Math.max(0, Number(sessionsTotalCount || 0));
    const ratio = count / limit;
    const tone = ratio >= 0.9 ? 'danger' : ratio >= 0.7 ? 'warning' : 'success';
    return { count, limit, ratio, tone };
  }, [selectedProjectSessionLimit, sessionsTotalCount]);

  const sessionsTotalPages = useMemo(
    () => Math.max(1, Math.ceil(Math.max(0, Number(sessionsTotalCount || 0)) / Math.max(1, Number(sessionsPerPage || 1)))),
    [sessionsPerPage, sessionsTotalCount]
  );

  const selectedSessionObject = useMemo(
    () => sessions.find((s) => s.session_id === expandedSession) || null,
    [sessions, expandedSession]
  );

  const selectedSessionExpiry = useMemo(() => {
    if (!expandedSession || flushes.length === 0) return null;
    const lastFlush = flushes[flushes.length - 1];
    if (!lastFlush?.received_at) return null;
    const dt = parseDate(lastFlush.received_at);
    if (Number.isNaN(dt.getTime())) return null;
    dt.setDate(dt.getDate() + selectedProjectRetentionDays);
    return dt;
  }, [expandedSession, flushes, selectedProjectRetentionDays]);

  const sessionMapPoints = useMemo(
    () => projectPositionsFromFlushes(flushes),
    [flushes]
  );

  const effectiveMapConfig = useMemo(() => ({
    ...(projectMap || {}),
    worldMinX: mapBounds.worldMinX,
    worldMaxX: mapBounds.worldMaxX,
    worldMinZ: mapBounds.worldMinZ,
    worldMaxZ: mapBounds.worldMaxZ,
    rotationDegrees: mapBounds.rotationDegrees,
    flipX: mapBounds.flipX,
    flipZ: mapBounds.flipZ,
  }), [projectMap, mapBounds]);

  const actionTimelineItems = useMemo(
    () => timeline.filter((item) => item.kind === 'action'),
    [timeline]
  );

  const timelineItemsPerPage = 50;

  const timelineTotalPages = useMemo(
    () => Math.max(1, Math.ceil(actionTimelineItems.length / timelineItemsPerPage)),
    [actionTimelineItems.length]
  );

  const pagedTimelineItems = useMemo(() => {
    const startIndex = (timelinePage - 1) * timelineItemsPerPage;
    return actionTimelineItems.slice(startIndex, startIndex + timelineItemsPerPage);
  }, [actionTimelineItems, timelinePage]);

  const handleTimelineItemToggle = useCallback((key) => {
    setExpandedTimelineItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }, []);

  const handleTimelineItemFocus = useCallback((key) => {
    setSelectedActionMapKey((prev) => (prev === key ? null : key));
  }, []);

  const selectedTimelineItem = useMemo(
    () => timeline.find((item) => item.key === selectedActionMapKey) || null,
    [timeline, selectedActionMapKey]
  );

  const projectedMapPoints = useMemo(() => {
    if (!projectMap?.hasImage || !mapImageSize.width || !mapImageSize.height) return [];
    const visiblePoints = selectedActionMapKey
      ? sessionMapPoints.filter((point) => point.t <= (selectedTimelineItem?.t || 0))
      : sessionMapPoints;
    return visiblePoints
      .map((point) => ({
        ...point,
        map: worldToMapPoint(point, effectiveMapConfig, mapImageSize.width, mapImageSize.height),
      }))
      .filter((point) => point.map);
  }, [projectMap, effectiveMapConfig, mapImageSize, sessionMapPoints, selectedActionMapKey, selectedTimelineItem]);

  const debugMapPoint = useMemo(() => {
    if (!projectMap?.hasImage || !mapImageSize.width || !mapImageSize.height) return null;
    if (String(debugPosition.x).trim() === '' || String(debugPosition.z).trim() === '') return null;
    const x = Number(debugPosition.x);
    const z = Number(debugPosition.z);
    if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
    return worldToMapPoint({ x, z }, effectiveMapConfig, mapImageSize.width, mapImageSize.height);
  }, [debugPosition, projectMap, effectiveMapConfig, mapImageSize]);

  const selectedProjectStats = useMemo(() => ({
    sessions: sessions.length,
    actions: sessions.reduce((sum, s) => sum + Number(s.action_events_count || 0), 0),
    activeTimelineActions: timeline.filter((item) => item.kind === 'action').length,
    mapPoints: projectedMapPoints.length,
  }), [sessions, timeline, projectedMapPoints]);

  const openedSessionTrackableFields = useMemo(
    () => buildTrackableFieldCatalog(timeline),
    [timeline]
  );

  const availableTrackedFields = useMemo(
    () => (fieldDiscoveryScope === 'opened_session' ? openedSessionTrackableFields : projectDiscoveredFields),
    [fieldDiscoveryScope, openedSessionTrackableFields, projectDiscoveredFields]
  );

  const fallbackOpenedSessionTrackedStatSeries = useMemo(() => {
    const out = {};
    trackedStats.forEach((stat) => {
      out[stat.id] = buildTrackedFieldSeries(timeline, stat.field_path, stat.value_type);
    });
    return out;
  }, [timeline, trackedStats]);

  const sessionsSummaryStats = useMemo(() => {
    if (sessions.length === 0) {
      return {
        avgDurationLabel: '-',
        sessionsPerHourLabel: '-',
        avgActionsLabel: '-',
      };
    }

    const durationValues = sessions
      .map((session) => getSessionDurationSeconds(session))
      .filter((value) => Number.isFinite(value) && value >= 0);

    const avgDurationSeconds = durationValues.length > 0
      ? durationValues.reduce((sum, value) => sum + value, 0) / durationValues.length
      : null;

    const timestamps = sessions
      .flatMap((session) => [session?.join_time, session?.quit_time, session?.created_at])
      .map((value) => {
        if (!value) return null;
        if (typeof value === 'number') return value;
        const asNumber = Number(value);
        if (Number.isFinite(asNumber) && asNumber > 1000000000) {
          return asNumber / 1000;
        }
        if (Number.isFinite(asNumber) && asNumber > 0) {
          return asNumber;
        }
        const parsed = parseDate(value).getTime();
        return Number.isFinite(parsed) ? parsed / 1000 : null;
      })
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((a, b) => a - b);

    let sessionsPerHour = null;
    if (timestamps.length >= 2) {
      const spanSeconds = Math.max(3600, timestamps[timestamps.length - 1] - timestamps[0]);
      sessionsPerHour = sessions.length / (spanSeconds / 3600);
    } else if (timestamps.length === 1) {
      sessionsPerHour = sessions.length;
    }

    const avgActions = sessions.reduce((sum, session) => sum + Number(session.action_events_count || 0), 0) / sessions.length;

    return {
      avgDurationLabel: avgDurationSeconds == null ? '-' : formatDurationFromSeconds(avgDurationSeconds),
      sessionsPerHourLabel: sessionsPerHour == null ? '-' : sessionsPerHour.toFixed(sessionsPerHour >= 10 ? 0 : 1),
      avgActionsLabel: Number.isFinite(avgActions) ? avgActions.toFixed(avgActions >= 10 ? 0 : 1) : '-',
    };
  }, [sessions]);

  const fetchJson = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        Authorization: `Bearer ${user?.token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      credentials: 'include',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.message || `Request failed (${res.status})`);
    return data;
  }, [user?.token]);

  const loadPlatformAdmins = useCallback(async () => {
    if (!accessInfo.platformAdmin || !user?.token) {
      setTeamLoading(false);
      setTeam({ db: [], envAllowlist: null });
      return;
    }
    setTeamLoading(true);
    try {
      const data = await fetchJson('/api/analytics/admins');
      setTeam({ db: data.db || [], envAllowlist: data.envAllowlist });
    } catch {
      setTeam({ db: [], envAllowlist: null });
    } finally {
      setTeamLoading(false);
    }
  }, [accessInfo.platformAdmin, fetchJson, user?.token]);

  const loadWorkspaceMembers = useCallback(async (workspaceId) => {
    if (!workspaceId) {
      setWorkspaceMembers([]);
      return;
    }
    try {
      const data = await fetchJson(`/api/analytics/workspaces/${workspaceId}/members`);
      setWorkspaceMembers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setWorkspaceMembers([]);
    }
  }, [fetchJson]);

  const loadEverything = useCallback(async () => {
    if (!user?.token) return;
    setLoading(true);
    setError('');
    try {
      const [access, plansData, workspacesData, projectsData] = await Promise.all([
        fetchJson('/api/analytics/access'),
        fetchJson('/api/analytics/plans'),
        fetchJson('/api/analytics/workspaces'),
        fetchJson('/api/analytics/projects'),
      ]);

      setAccessInfo(access);
      setPlans(Array.isArray(plansData) ? plansData : []);
      const nextWorkspaces = Array.isArray(workspacesData) ? workspacesData : [];
      setWorkspaces(nextWorkspaces);
      setProjects(Array.isArray(projectsData) ? projectsData : []);

      if (nextWorkspaces.length > 0) {
        const nextWorkspaceId = selectedWorkspaceId && nextWorkspaces.some((w) => w.id === selectedWorkspaceId)
          ? selectedWorkspaceId
          : nextWorkspaces[0].id;
        setSelectedWorkspaceId(nextWorkspaceId);
        await loadWorkspaceMembers(nextWorkspaceId);
      } else {
        setSelectedWorkspaceId('');
        setWorkspaceMembers([]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [fetchJson, loadWorkspaceMembers, selectedWorkspaceId, user?.token]);

  useEffect(() => {
    loadEverything();
  }, [loadEverything]);

  useEffect(() => {
    loadPlatformAdmins();
  }, [loadPlatformAdmins]);

  useEffect(() => {
    if (selectedWorkspaceId) {
      loadWorkspaceMembers(selectedWorkspaceId);
    }
  }, [selectedWorkspaceId, loadWorkspaceMembers]);

  useEffect(() => {
    if (!selectedProject) {
      setProjectRetentionInput('');
      setProjectSessionLimitInput('');
      return;
    }
    const currentRetention = selectedProject.custom_retention_days ?? selectedProjectRetentionDays;
    const currentSessionLimit = selectedProject.custom_session_limit ?? selectedProjectSessionLimit;
    setProjectRetentionInput(String(currentRetention || ''));
    setProjectSessionLimitInput(String(currentSessionLimit || ''));
  }, [selectedProject, selectedProjectRetentionDays, selectedProjectSessionLimit]);

  useEffect(() => {
    if (!selectedProjectId || !user?.token) {
      setTrackedStats([]);
      return;
    }

    let cancelled = false;
    setTrackedStatsLoading(true);
    fetch(`${API_BASE}/api/analytics/projects/${selectedProjectId}/tracked-stats`, {
      headers: {
        Authorization: `Bearer ${user.token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (!ok) throw new Error(data?.error || 'Failed to load tracked stats');
        setTrackedStats(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setTrackedStats([]);
          setError(err.message || 'Failed to load tracked stats');
        }
      })
      .finally(() => {
        if (!cancelled) setTrackedStatsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, user?.token]);

  useEffect(() => {
    if (!selectedProjectId || !user?.token) {
      setProjectDiscoveredFields([]);
      return;
    }

    let cancelled = false;
    setProjectDiscoveredFieldsLoading(true);
    fetch(`${API_BASE}/api/analytics/projects/${selectedProjectId}/discover-fields`, {
      headers: {
        Authorization: `Bearer ${user.token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (cancelled) return;
        if (!ok) throw new Error(data?.error || 'Failed to discover fields');
        setProjectDiscoveredFields(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setProjectDiscoveredFields([]);
          setError(err.message || 'Failed to discover project fields');
        }
      })
      .finally(() => {
        if (!cancelled) setProjectDiscoveredFieldsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, user?.token]);

  useEffect(() => {
    if (!selectedProjectId || !user?.token || trackedStats.length === 0) {
      setTrackedStatSeriesMap({});
      return;
    }

    let cancelled = false;
    Promise.all(
      trackedStats.map(async (stat) => {
        const params = new URLSearchParams({
          fieldPath: stat.field_path,
          mode: trackedStatsViewMode,
        });
        if (trackedStatsViewMode === 'opened_session' && expandedSession) {
          params.set('sessionId', expandedSession);
        }
        const response = await fetch(`${API_BASE}/api/analytics/projects/${selectedProjectId}/tracked-stats/series?${params.toString()}`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data?.error || `Failed to load series for ${stat.label}`);
        return [stat.id, Array.isArray(data?.points) ? data.points : []];
      })
    )
      .then((entries) => {
        if (!cancelled) {
          setTrackedStatSeriesMap(Object.fromEntries(entries));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setTrackedStatSeriesMap({});
          setError(err.message || 'Failed to load tracked stat data');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, user?.token, trackedStats, trackedStatsViewMode, expandedSession]);

  // Keep a project of the current workspace selected so explore views are never empty by accident.
  useEffect(() => {
    if (loading) return;
    if (workspaceProjects.length === 0) {
      if (selectedProjectId) setSelectedProjectId(null);
      return;
    }
    if (!workspaceProjects.some((p) => p.id === selectedProjectId)) loadSessions(workspaceProjects[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, workspaceProjects, selectedProjectId]);

  useEffect(() => {
    if (keyModal) setNewProjectOpen(false);
  }, [keyModal]);

  const createWorkspace = async (e) => {
    e.preventDefault();
    if (!newWorkspaceName.trim() || !newWorkspaceOwnerEmail.trim()) return;
    setWorkspaceSaving(true);
    setError('');
    try {
      await fetchJson('/api/analytics/workspaces', {
        method: 'POST',
        body: JSON.stringify({
          name: newWorkspaceName.trim(),
          ownerEmail: newWorkspaceOwnerEmail.trim(),
          planId: newWorkspacePlanId,
        }),
      });
      setNewWorkspaceName('');
      setNewWorkspaceOwnerEmail('');
      setNewWorkspacePlanId(plans[0]?.id || 'starter');
      await loadEverything();
    } catch (e) {
      setError(e.message);
    } finally {
      setWorkspaceSaving(false);
    }
  };

  const updateWorkspacePlan = async (workspaceId, planId) => {
    setError('');
    try {
      await fetchJson(`/api/analytics/workspaces/${workspaceId}`, {
        method: 'PUT',
        body: JSON.stringify({ planId }),
      });
      await loadEverything();
    } catch (e) {
      setError(e.message);
    }
  };

  const updateWorkspaceStatus = async (workspaceId, status) => {
    setError('');
    try {
      await fetchJson(`/api/analytics/workspaces/${workspaceId}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      await loadEverything();
    } catch (e) {
      setError(e.message);
    }
  };

  const saveProjectRetention = async (e) => {
    e?.preventDefault?.();
    if (!selectedProjectId) return;
    setProjectRetentionSaving(true);
    setError('');
    try {
      await fetchJson(`/api/analytics/projects/${selectedProjectId}`, {
        method: 'PUT',
        body: JSON.stringify({ customRetentionDays: Number(projectRetentionInput) }),
      });
      await loadEverything();
    } catch (e) {
      setError(e.message);
    } finally {
      setProjectRetentionSaving(false);
    }
  };

  const saveProjectSessionLimit = async (e) => {
    e?.preventDefault?.();
    if (!selectedProjectId) return;
    setProjectSessionLimitSaving(true);
    setError('');
    try {
      await fetchJson(`/api/analytics/projects/${selectedProjectId}`, {
        method: 'PUT',
        body: JSON.stringify({ customSessionLimit: Number(projectSessionLimitInput) }),
      });
      await loadEverything();
    } catch (e) {
      setError(e.message);
    } finally {
      setProjectSessionLimitSaving(false);
    }
  };

  const addWorkspaceMember = async (e) => {
    e.preventDefault();
    if (!selectedWorkspaceId || !workspaceMemberEmail.trim()) return;
    setMemberSaving(true);
    setError('');
    try {
      await fetchJson(`/api/analytics/workspaces/${selectedWorkspaceId}/members`, {
        method: 'POST',
        body: JSON.stringify({
          email: workspaceMemberEmail.trim(),
          role: workspaceMemberRole,
        }),
      });
      setWorkspaceMemberEmail('');
      setWorkspaceMemberRole('viewer');
      await loadWorkspaceMembers(selectedWorkspaceId);
      await loadEverything();
    } catch (e) {
      setError(e.message);
    } finally {
      setMemberSaving(false);
    }
  };

  const removeWorkspaceMember = async (email) => {
    if (!selectedWorkspaceId) return;
    if (!(await ask.confirm({
      title: 'Remove member?',
      message: `${email} will lose access to this workspace and all its projects.`,
      confirmLabel: 'Remove member',
      danger: true,
    }))) return;
    setError('');
    try {
      await fetchJson(`/api/analytics/workspaces/${selectedWorkspaceId}/members?email=${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      await loadWorkspaceMembers(selectedWorkspaceId);
      await loadEverything();
    } catch (e) {
      setError(e.message);
    }
  };

  const createProject = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name || !selectedWorkspaceId) return;
    setCreating(true);
    setError('');
    try {
      const data = await fetchJson('/api/analytics/projects', {
        method: 'POST',
        body: JSON.stringify({ name, workspaceId: selectedWorkspaceId }),
      });
      setKeyModal({
        projectId: data.projectId,
        apiKey: data.apiKey,
        name: data.name,
        workspaceName: data.workspaceName,
        endpoint: data.endpoint,
      });
      setNewName('');
      await loadEverything();
    } catch (e) {
      setError(e.message);
    } finally {
      setCreating(false);
    }
  };

  const rotateKey = async (projectId) => {
    if (!(await ask.confirm({
      title: 'Rotate API key?',
      message: 'The current key stops working immediately. Your game cannot send data until it uses the new key.',
      confirmLabel: 'Rotate key',
      danger: true,
    }))) return;
    setError('');
    try {
      const data = await fetchJson(`/api/analytics/projects/${projectId}/rotate-key`, {
        method: 'POST',
      });
      setKeyModal({
        projectId: data.projectId,
        apiKey: data.apiKey,
        name: projects.find((p) => p.id === projectId)?.name || '',
        workspaceName: projects.find((p) => p.id === projectId)?.workspace_name || '',
        endpoint: INGEST_ENDPOINT,
      });
    } catch (e) {
      setError(e.message);
    }
  };

  const renameEntity = async (kind, id, currentName) => {
    const name = await ask.prompt({
      title: `Rename ${kind}`,
      label: `${kind.charAt(0).toUpperCase()}${kind.slice(1)} name`,
      defaultValue: currentName,
      confirmLabel: 'Save name',
    });
    if (!name || name === currentName) return;
    setError('');
    try {
      await fetchJson(`/api/analytics/${kind}s/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ name }),
      });
      await loadEverything();
    } catch (e) {
      setError(e.message);
    }
  };

  // Deletion is permanent (all sessions, funnels and keys go), so the name must be typed back.
  const deleteEntity = async (kind, id, name, warning) => {
    if (!(await ask.confirm({
      title: `Delete ${kind} "${name}"?`,
      message: `${warning}\nThis action is permanent.`,
      requireText: name,
      confirmLabel: `Delete ${kind}`,
      danger: true,
    }))) return;
    setError('');
    try {
      await fetchJson(`/api/analytics/${kind}s/${id}`, { method: 'DELETE' });
      const removedProjectIds = kind === 'project' ? [id] : projects.filter((p) => p.workspace_id === id).map((p) => p.id);
      if (removedProjectIds.includes(selectedProjectId)) setSelectedProjectId(null);
      if (keyModal && removedProjectIds.includes(keyModal.projectId)) setKeyModal(null);
      await loadEverything();
    } catch (e) {
      setError(e.message);
    }
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };


  const loadProjectMap = useCallback(async (projectId) => {
    if (!projectId) {
      setProjectMap(null);
      setMapBounds({ worldMinX: '', worldMaxX: '', worldMinZ: '', worldMaxZ: '', rotationDegrees: 0, flipX: false, flipZ: false });
      setMapFile(null);
      setMapImageSize({ width: 0, height: 0 });
      setSelectedActionMapKey(null);
      return;
    }
    setMapLoading(true);
    try {
      const data = await fetchJson(`/api/analytics/projects/${projectId}/map`);
      setProjectMap(data);
      setMapBounds({
        worldMinX: data.worldMinX ?? '',
        worldMaxX: data.worldMaxX ?? '',
        worldMinZ: data.worldMinZ ?? '',
        worldMaxZ: data.worldMaxZ ?? '',
        rotationDegrees: data.rotationDegrees ?? 0,
        flipX: Boolean(data.flipX),
        flipZ: Boolean(data.flipZ),
      });
      setMapImageSize({ width: 0, height: 0 });
      setSelectedActionMapKey(null);
    } catch (e) {
      setError(e.message);
      setProjectMap(null);
    } finally {
      setMapLoading(false);
    }
  }, [fetchJson]);

  const saveProjectMap = async (e) => {
    e?.preventDefault?.();
    if (!selectedProjectId) return;
    setMapSaving(true);
    setError('');
    try {
      const form = new FormData();
      form.append('worldMinX', String(mapBounds.worldMinX));
      form.append('worldMaxX', String(mapBounds.worldMaxX));
      form.append('worldMinZ', String(mapBounds.worldMinZ));
      form.append('worldMaxZ', String(mapBounds.worldMaxZ));
      form.append('rotationDegrees', String(mapBounds.rotationDegrees ?? 0));
      form.append('flipX', String(Boolean(mapBounds.flipX)));
      form.append('flipZ', String(Boolean(mapBounds.flipZ)));
      if (mapFile) {
        form.append('file', mapFile);
      }

      const res = await fetch(`${API_BASE}/api/analytics/projects/${selectedProjectId}/map`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        body: form,
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to save map');
      setMapFile(null);
      setMapVersion((v) => v + 1);
      await loadProjectMap(selectedProjectId);
    } catch (e) {
      setError(e.message);
    } finally {
      setMapSaving(false);
    }
  };

  const loadSessions = async (projectId, options = {}) => {
    const nextPage = Math.max(1, Number(options.page || sessionsPage || 1));
    const nextPerPage = Math.max(1, Number(options.perPage || sessionsPerPage));
    const nextSortBy = String(options.sortBy || sessionsSortBy || 'updated_at');
    const nextSortDir = String(options.sortDir || sessionsSortDir || 'desc');
    const shouldReloadMap = options.reloadMap !== false;
    setSelectedProjectId(projectId);
    setSessionsPage(nextPage);
    setSessionsPerPage(nextPerPage);
    setSessionsSortBy(nextSortBy);
    setSessionsSortDir(nextSortDir);
    if (shouldReloadMap) {
      await loadProjectMap(projectId);
    }
    setSessionsLoading(true);
    setExpandedSession(null);
    setFlushes([]);
    setTimeline([]);
    setExpandedTimelineItems({});
    setSelectedActionMapKey(null);
    setShowTrackedStatBuilder(false);
    setFieldDiscoveryScope('all_sessions');
    setTrackedStatsViewMode('all_sessions');
    setTrackedStatSeriesMap({});
    setTrackedStatForm({ fieldPath: '', label: '', valueType: 'number', visualization: 'line' });
    setError('');
    try {
      const params = new URLSearchParams({
        limit: String(nextPerPage),
        page: String(nextPage),
        sortBy: nextSortBy,
        sortDir: nextSortDir,
      });
      const data = await fetchJson(`/api/analytics/projects/${projectId}/sessions?${params.toString()}`);
      setSessions(Array.isArray(data?.items) ? data.items : []);
      setSessionsTotalCount(Number(data?.total_session_count || 0));
    } catch (e) {
      setError(e.message);
      setSessions([]);
      setSessionsTotalCount(0);
    } finally {
      setSessionsLoading(false);
    }
  };

  const reloadCurrentSessionsPage = useCallback(async (projectId = selectedProjectId, nextPage = sessionsPage) => {
    if (!projectId) return;
    const params = new URLSearchParams({
      limit: String(sessionsPerPage),
      page: String(nextPage),
      sortBy: sessionsSortBy,
      sortDir: sessionsSortDir,
    });
    const data = await fetchJson(`/api/analytics/projects/${projectId}/sessions?${params.toString()}`);
    setSessions(Array.isArray(data?.items) ? data.items : []);
    setSessionsTotalCount(Number(data?.total_session_count || 0));
  }, [fetchJson, selectedProjectId, sessionsPage, sessionsPerPage, sessionsSortBy, sessionsSortDir]);

  const deleteSession = async (sessionId) => {
    if (!selectedProjectId) return;
    if (!(await ask.confirm({
      title: 'Delete this session?',
      message: 'Its summary and all raw flushes are removed. Funnel and heatmap totals are not affected.',
      confirmLabel: 'Delete session',
      danger: true,
    }))) return;
    setError('');
    try {
      await fetchJson(`/api/analytics/projects/${selectedProjectId}/sessions/${encodeURIComponent(sessionId)}`, {
        method: 'DELETE',
      });
      if (expandedSession === sessionId) {
        setExpandedSession(null);
        setFlushes([]);
        setTimeline([]);
        setExpandedTimelineItems({});
        setSelectedActionMapKey(null);
      }
      await reloadCurrentSessionsPage();
    } catch (e) {
      setError(e.message);
    }
  };

  const bulkDeleteSessionsByAge = async () => {
    if (!selectedProjectId) return;
    const olderThanDays = Number(bulkDeleteAgeDays);
    if (!Number.isInteger(olderThanDays) || olderThanDays < 1) return;
    if (!(await ask.confirm({
      title: `Delete sessions older than ${olderThanDays} day${olderThanDays === 1 ? '' : 's'}?`,
      message: `Every session in ${selectedProject?.name || 'this project'} older than that is removed, together with its raw flushes.`,
      confirmLabel: 'Delete sessions',
      danger: true,
    }))) return;
    setBulkDeleteSessionsLoading(true);
    setError('');
    try {
      const result = await fetchJson(`/api/analytics/projects/${selectedProjectId}/sessions/bulk-delete?olderThanDays=${encodeURIComponent(String(olderThanDays))}`, {
        method: 'DELETE',
      });
      if (expandedSession && sessions.some((session) => session.session_id === expandedSession)) {
        const cutoffSeconds = Math.floor(Date.now() / 1000) - (olderThanDays * 86400);
        const expandedRow = sessions.find((session) => session.session_id === expandedSession);
        const expandedSessionTime = Number(expandedRow?.quit_time || expandedRow?.join_time || 0);
        if (expandedSessionTime > 0 && expandedSessionTime < cutoffSeconds) {
          setExpandedSession(null);
          setFlushes([]);
          setTimeline([]);
          setExpandedTimelineItems({});
          setSelectedActionMapKey(null);
          setTimelinePage(1);
        }
      }
      await reloadCurrentSessionsPage(selectedProjectId, 1);
      setSessionsPage(1);
      if (result?.deletedSessions === 0) {
        setError('No sessions matched that age filter.');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBulkDeleteSessionsLoading(false);
    }
  };

  const exportProjectSessionsRaw = async () => {
    if (!selectedProjectId || !selectedProject || !user?.token) return;
    setExportSessionsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (exportSessionsMode === 'count') {
        const count = Number(exportSessionsCount);
        if (!Number.isInteger(count) || count < 1) {
          throw new Error('Choose a valid number of sessions to export');
        }
        params.set('limit', String(count));
      }
      if (includeAiPromptInExport) {
        params.set('includeAiPrompt', '1');
      }
      if (!includePositionsInExport) {
        params.set('includePositions', '0');
      }

      const query = params.toString();
      const response = await fetch(`${API_BASE}/api/analytics/projects/${selectedProjectId}/sessions/export${query ? `?${query}` : ''}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.error || 'Failed to export project sessions');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers.get('Content-Disposition') || '';
      const match = contentDisposition.match(/filename="?([^";]+)"?/i);
      const fallbackName = `${selectedProject.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'project'}-raw-sessions-${new Date().toISOString().slice(0, 10)}.json`;
      const filename = match?.[1] || fallbackName;

      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || 'Failed to export project sessions');
    } finally {
      setExportSessionsLoading(false);
    }
  };

  const loadFlushes = async (projectId, sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
      setFlushes([]);
      setTimeline([]);
      setExpandedTimelineItems({});
      setSelectedActionMapKey(null);
      setTimelinePage(1);
      return;
    }
    setExpandedSession(sessionId);
    setError('');
    setFlushes([]);
    setTimeline([]);
    setFlushesLoading(true);
    try {
      const data = await fetchJson(`/api/analytics/projects/${projectId}/sessions/${encodeURIComponent(sessionId)}/flushes`);
      const nextFlushes = Array.isArray(data) ? data : [];
      setFlushes(nextFlushes);
      setTimeline(buildSessionTimeline(nextFlushes));
      setExpandedTimelineItems({});
      setSelectedActionMapKey(null);
      setTimelinePage(1);
    } catch (e) {
      setError(e.message);
      setFlushes([]);
      setTimeline([]);
      setExpandedTimelineItems({});
      setTimelinePage(1);
    } finally {
      setFlushesLoading(false);
    }
  };

  const addAdminEmail = async (e) => {
    e.preventDefault();
    const em = newAdminEmail.trim().toLowerCase();
    if (!em) return;
    setTeamSaving(true);
    setError('');
    try {
      await fetchJson('/api/analytics/admins', {
        method: 'POST',
        body: JSON.stringify({ email: em }),
      });
      setNewAdminEmail('');
      await loadPlatformAdmins();
    } catch (err) {
      setError(err.message);
    } finally {
      setTeamSaving(false);
    }
  };

  const removeAdminEmail = async (emailAddr, force = false) => {
    if (!(await ask.confirm({
      title: force ? 'Force-remove admin access?' : 'Remove platform admin?',
      message: `${emailAddr} will no longer be able to create or manage customer workspaces.`,
      confirmLabel: force ? 'Force remove' : 'Remove admin',
      danger: true,
    }))) return;
    setError('');
    try {
      const qs = new URLSearchParams({ email: emailAddr });
      if (force) qs.set('force', '1');
      await fetchJson(`/api/analytics/admins?${qs.toString()}`, {
        method: 'DELETE',
      });
      await loadPlatformAdmins();
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleTimelineItem = (key) => {
    setExpandedTimelineItems((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const createTrackedStat = async (e) => {
    e.preventDefault();
    if (!selectedProjectId || !trackedStatForm.fieldPath.trim() || !trackedStatForm.label.trim()) return;
    setTrackedStatsSaving(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/analytics/projects/${selectedProjectId}/tracked-stats`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${user?.token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(trackedStatForm),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Failed to create tracked stat');
      setTrackedStats((prev) => [...prev, data]);
      setTrackedStatForm({ fieldPath: '', label: '', valueType: 'number', visualization: 'line' });
      setShowTrackedStatBuilder(false);
    } catch (err) {
      setError(err.message || 'Failed to create tracked stat');
    } finally {
      setTrackedStatsSaving(false);
    }
  };

  const deleteTrackedStat = async (statId) => {
    if (!selectedProjectId || !statId) return;
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/analytics/projects/${selectedProjectId}/tracked-stats/${statId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${user?.token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Failed to delete tracked stat');
      setTrackedStats((prev) => prev.filter((stat) => stat.id !== statId));
    } catch (err) {
      setError(err.message || 'Failed to delete tracked stat');
    }
  };

  // ---------- render ----------

  if (loading) {
    return (
      <Page aria-busy="true">
        <LoadingLayout>
          <div className="side">
            <Skeleton $h="3.25rem" $r="0.5rem" />
            <Skeleton $h="2.5rem" $r="0.5rem" />
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} $h="1.75rem" $w={`${80 - i * 8}%`} />)}
          </div>
          <div className="main">
            <Skeleton $h="2rem" $w="16rem" />
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              <Skeleton $h="24rem" $r="0.75rem" style={{ flex: '1 1 20rem' }} />
              <Skeleton $h="24rem" $r="0.75rem" style={{ flex: '2 1 30rem' }} />
            </div>
          </div>
        </LoadingLayout>
        <VisuallyHidden role="status">Loading analytics…</VisuallyHidden>
      </Page>
    );
  }

  if (!accessInfo.allowed) {
    return (
      <Page>
        <CenteredNote>
          <Empty>
            <strong>Analytics access required</strong>
            Your account does not have access to an analytics workspace yet. If you have already paid, ask the platform admin to create a
            workspace with your email as the owner.
            {error && <Notice $tone="error" style={{ marginTop: '1rem' }}>{error}</Notice>}
          </Empty>
        </CenteredNote>
      </Page>
    );
  }

  const canManageWorkspace = (workspace) => accessInfo.platformAdmin
    || ['owner', 'admin'].includes(String(workspace?.membership_role || '').toLowerCase());
  const canManageWorkspaces = workspaces.some(canManageWorkspace);
  const isPlatformAdmin = Boolean(accessInfo.platformAdmin);

  const activeSection = (section === 'workspace' && !canManageWorkspaces) || (section === 'platform' && !isPlatformAdmin)
    ? 'sessions'
    : section;
  const projectSection = ['sessions', 'funnels', 'heatmaps', 'settings'].includes(activeSection);

  const openMapSettings = () => {
    setSettingsTab('map');
    setSection('settings');
  };
  const startNewProject = () => {
    setSection('workspace');
    setNewProjectOpen(true);
  };

  const TITLES = {
    sessions: 'Sessions',
    funnels: 'Funnels',
    heatmaps: 'Heatmaps',
    settings: 'Project settings',
    workspace: 'Workspace',
    platform: 'Platform admin',
  };
  const crumb = activeSection === 'platform'
    ? 'All workspaces'
    : activeSection === 'workspace'
      ? selectedWorkspace?.name || ''
      : [selectedWorkspace?.name, selectedProject?.name].filter(Boolean).join(' / ');

  const rangeOptions = activeSection === 'funnels' ? RANGE_OPTIONS : RANGE_OPTIONS.filter((o) => o.value !== 'custom');
  const effectiveRange = activeSection === 'heatmaps' && rangeValue === 'custom' ? '7' : rangeValue;

  const topActions = !selectedProject
    ? null
    : activeSection === 'sessions'
      ? (
        <>
          <GhostBtn type="button" $small onClick={() => loadSessions(selectedProject.id, { reloadMap: false })}>
            Refresh
          </GhostBtn>
          <GhostBtn
            type="button"
            $small
            onClick={() => {
              setSettingsTab('data');
              setSection('settings');
            }}
          >
            Export JSON
          </GhostBtn>
        </>
      )
      : activeSection === 'funnels' || activeSection === 'heatmaps'
        ? (
          <Segmented role="group" aria-label="Time range">
            {rangeOptions.map((o) => (
              <Segment key={o.value} type="button" $active={effectiveRange === o.value} aria-pressed={effectiveRange === o.value} onClick={() => setRangeValue(o.value)}>
                {o.label}
              </Segment>
            ))}
          </Segmented>
        )
        : null;

  const mapProps = {
    projectMap,
    mapLoading,
    mapVersion,
    userToken: user?.token,
    setMapImageSize,
    mapImageSize,
    projectedMapPoints,
    selectedActionMapKey,
    debugMapPoint,
    viewportResetKey: expandedSession,
  };

  const keyForSelected = keyModal && keyModal.projectId === selectedProjectId ? keyModal.apiKey : null;
  const showKeyBanner = keyModal && !(activeSection === 'settings' && settingsTab === 'connect' && keyForSelected);

  return (
    <AnalyticsShell
      workspaces={workspaces}
      selectedWorkspace={selectedWorkspace}
      onSelectWorkspace={setSelectedWorkspaceId}
      projects={workspaceProjects}
      selectedProject={selectedProject}
      onSelectProject={(id) => loadSessions(id)}
      onNewProject={startNewProject}
      section={activeSection}
      onNavigate={setSection}
      canManageWorkspaces={canManageWorkspaces}
      isPlatformAdmin={isPlatformAdmin}
      sessionUsage={selectedProject ? selectedProjectSessionUsage : null}
      retentionDays={selectedProjectRetentionDays}
      crumb={crumb}
      title={TITLES[activeSection]}
      actions={topActions}
    >
      {dialog}
      <Stack $gap="1.25rem">
        {error && (
          <Notice $tone="error" role="alert" style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'center' }}>
            <span>{error}</span>
            <GhostBtn type="button" $small onClick={() => setError('')} style={{ borderColor: 'rgba(240,82,82,0.5)', color: '#f07a7a' }}>
              Dismiss
            </GhostBtn>
          </Notice>
        )}

        {showKeyBanner && (
          <Card style={{ borderColor: 'rgba(245,197,66,0.45)' }}>
            <Row $justify="space-between" $align="flex-start" style={{ marginBottom: '0.75rem' }}>
              <div>
                <CardTitle>{keyModal.name ? `${keyModal.name} is ready` : 'New API key'}</CardTitle>
                <Hint $mb="0">Copy the API key now. It is shown only once.</Hint>
              </div>
              <Row>
                <GhostBtn
                  type="button"
                  $small
                  onClick={() => {
                    loadSessions(keyModal.projectId);
                    setSettingsTab('connect');
                    setSection('settings');
                  }}
                >
                  Open setup
                </GhostBtn>
                <GhostBtn type="button" $small onClick={() => setKeyModal(null)}>
                  Done
                </GhostBtn>
              </Row>
            </Row>
            <Stack $gap="0.5rem">
              {[
                ['Endpoint', keyModal.endpoint || INGEST_ENDPOINT],
                ['Project ID', keyModal.projectId],
                ['API key', keyModal.apiKey],
              ].map(([label, value]) => (
                <Row key={label} style={{ flexWrap: 'nowrap' }}>
                  <Mono style={{ width: '5.5rem', flexShrink: 0 }}>{label}</Mono>
                  <Mono $bright style={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</Mono>
                  <GhostBtn type="button" $small onClick={() => copyText(value)}>Copy</GhostBtn>
                </Row>
              ))}
              <Row $justify="flex-end">
                <GhostBtn type="button" $small onClick={() => copyText(buildRobloxSnippet(keyModal.projectId, keyModal.apiKey))}>
                  Copy Roblox snippet
                </GhostBtn>
              </Row>
            </Stack>
          </Card>
        )}

        {projectSection && !selectedProject ? (
          <Empty>
            <strong>{workspaceProjects.length ? 'Pick a project' : 'No projects yet'}</strong>
            <p style={{ marginBottom: canManageWorkspaces && !workspaceProjects.length ? '1rem' : 0 }}>
              {workspaceProjects.length
                ? 'Choose a game from the project menu on the left.'
                : 'Create a project to get an API key, then connect your game.'}
            </p>
            {canManageWorkspaces && !workspaceProjects.length && (
              <Btn type="button" onClick={startNewProject}>Create a project</Btn>
            )}
          </Empty>
        ) : activeSection === 'sessions' ? (
          <SessionsView
            sessions={sessions}
            sessionsLoading={sessionsLoading}
            sessionsTotalCount={sessionsTotalCount}
            sessionsPage={sessionsPage}
            sessionsPerPage={sessionsPerPage}
            sessionsTotalPages={sessionsTotalPages}
            sessionsSortBy={sessionsSortBy}
            sessionsSortDir={sessionsSortDir}
            onChangeList={(opts) => loadSessions(selectedProject.id, {
              page: opts.page ?? sessionsPage,
              perPage: opts.perPage ?? sessionsPerPage,
              sortBy: opts.sortBy ?? sessionsSortBy,
              sortDir: opts.sortDir ?? sessionsSortDir,
              reloadMap: false,
            })}
            expandedSession={expandedSession}
            selectedSession={selectedSessionObject}
            flushesLoading={flushesLoading}
            flushCount={flushes.length}
            timeline={timeline}
            sessionExpiry={selectedSessionExpiry}
            onOpenSession={(sessionId) => loadFlushes(selectedProject.id, sessionId)}
            onDeleteSession={deleteSession}
            actionTimelineItems={actionTimelineItems}
            pagedTimelineItems={pagedTimelineItems}
            timelinePage={timelinePage}
            timelineTotalPages={timelineTotalPages}
            setTimelinePage={setTimelinePage}
            expandedTimelineItems={expandedTimelineItems}
            selectedActionMapKey={selectedActionMapKey}
            onToggleTimelineItem={handleTimelineItemToggle}
            onFocusTimelineItem={handleTimelineItemFocus}
            mapProps={mapProps}
            onOpenMapSettings={openMapSettings}
          />
        ) : activeSection === 'funnels' ? (
          <FunnelPanel projectId={selectedProjectId} fetchJson={fetchJson} windowValue={effectiveRange} />
        ) : activeSection === 'heatmaps' ? (
          <HeatmapPanel
            projectId={selectedProjectId}
            fetchJson={fetchJson}
            userToken={user?.token}
            windowValue={effectiveRange}
            onOpenMapSettings={openMapSettings}
          />
        ) : activeSection === 'settings' ? (
          <ProjectSettingsView
            tab={settingsTab}
            onTabChange={setSettingsTab}
            project={selectedProject}
            canManage={canManageWorkspace(selectedWorkspace)}
            copyText={copyText}
            retentionInput={projectRetentionInput}
            setRetentionInput={setProjectRetentionInput}
            retentionCurrent={selectedProjectRetentionDays}
            retentionMax={selectedProjectMaxRetentionDays}
            retentionSaving={projectRetentionSaving}
            onSaveRetention={saveProjectRetention}
            sessionLimitInput={projectSessionLimitInput}
            setSessionLimitInput={setProjectSessionLimitInput}
            sessionLimitCurrent={selectedProjectSessionLimit}
            sessionLimitMax={selectedProjectMaxSessionLimit}
            sessionLimitSaving={projectSessionLimitSaving}
            onSaveSessionLimit={saveProjectSessionLimit}
            onRenameProject={() => renameEntity('project', selectedProject.id, selectedProject.name)}
            mapBounds={mapBounds}
            setMapBounds={setMapBounds}
            mapFile={mapFile}
            setMapFile={setMapFile}
            mapSaving={mapSaving}
            onSaveMap={saveProjectMap}
            debugPosition={debugPosition}
            setDebugPosition={setDebugPosition}
            mapProps={{ ...mapProps, viewportResetKey: 'settings' }}
            revealedKey={keyForSelected}
            onRotateKey={() => rotateKey(selectedProject.id)}
            sessionsTotalCount={sessionsTotalCount}
            exportMode={exportSessionsMode}
            setExportMode={setExportSessionsMode}
            exportCount={exportSessionsCount}
            setExportCount={setExportSessionsCount}
            includeAiPrompt={includeAiPromptInExport}
            setIncludeAiPrompt={setIncludeAiPromptInExport}
            includePositions={includePositionsInExport}
            setIncludePositions={setIncludePositionsInExport}
            exportLoading={exportSessionsLoading}
            onExport={exportProjectSessionsRaw}
            bulkDeleteAgeDays={bulkDeleteAgeDays}
            setBulkDeleteAgeDays={setBulkDeleteAgeDays}
            bulkDeleteLoading={bulkDeleteSessionsLoading}
            onBulkDelete={bulkDeleteSessionsByAge}
            onDeleteProject={() => deleteEntity(
              'project',
              selectedProject.id,
              selectedProject.name,
              'This deletes the project with all its sessions, funnels, tracked stats and map. Its API key stops working immediately.'
            )}
          />
        ) : activeSection === 'workspace' ? (
          <WorkspaceView
            workspace={selectedWorkspace}
            projects={workspaceProjects}
            members={workspaceMembers}
            currentUserEmail={user?.email}
            canManage={canManageWorkspace(selectedWorkspace)}
            retentionDays={selectedWorkspaceRetentionDays}
            onOpenProject={(id) => {
              loadSessions(id);
              setSection('sessions');
            }}
            onRenameWorkspace={() => renameEntity('workspace', selectedWorkspace.id, selectedWorkspace.name)}
            onRenameProject={(p) => renameEntity('project', p.id, p.name)}
            onRotateKey={rotateKey}
            onDeleteProject={(p) => deleteEntity('project', p.id, p.name, 'This deletes the project with all its sessions, funnels, tracked stats and map. Its API key stops working immediately.')}
            copyText={copyText}
            newProjectOpen={newProjectOpen}
            setNewProjectOpen={setNewProjectOpen}
            newProjectName={newName}
            setNewProjectName={setNewName}
            creatingProject={creating}
            onCreateProject={createProject}
            memberEmail={workspaceMemberEmail}
            setMemberEmail={setWorkspaceMemberEmail}
            memberRole={workspaceMemberRole}
            setMemberRole={setWorkspaceMemberRole}
            memberSaving={memberSaving}
            onAddMember={addWorkspaceMember}
            onRemoveMember={removeWorkspaceMember}
          />
        ) : (
          <PlatformView
            workspaces={workspaces}
            plans={plans}
            onUpdatePlan={updateWorkspacePlan}
            onUpdateStatus={updateWorkspaceStatus}
            onRenameWorkspace={(w) => renameEntity('workspace', w.id, w.name)}
            onDeleteWorkspace={(w) => deleteEntity('workspace', w.id, w.name, `This deletes the workspace, its members and ALL ${w.project_count || 0} project(s) inside it with all their analytics data. Ingest for those projects stops immediately.`)}
            onOpenWorkspace={(id) => {
              setSelectedWorkspaceId(id);
              setSection('workspace');
            }}
            newWorkspaceName={newWorkspaceName}
            setNewWorkspaceName={setNewWorkspaceName}
            newWorkspaceOwnerEmail={newWorkspaceOwnerEmail}
            setNewWorkspaceOwnerEmail={setNewWorkspaceOwnerEmail}
            newWorkspacePlanId={newWorkspacePlanId}
            setNewWorkspacePlanId={setNewWorkspacePlanId}
            workspaceSaving={workspaceSaving}
            onCreateWorkspace={createWorkspace}
            admins={team.db || []}
            adminsLoading={teamLoading}
            newAdminEmail={newAdminEmail}
            setNewAdminEmail={setNewAdminEmail}
            adminSaving={teamSaving}
            onAddAdmin={addAdminEmail}
            onRemoveAdmin={removeAdminEmail}
          />
        )}
      </Stack>
    </AnalyticsShell>
  );
};

export default AnalyticsAdmin;
